import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BASE_URL } from "../constant";
import { store } from "./index";

export const stepsEnum = Object.freeze({
  0: "Cart",
  1: "Event",
  2: "Address",
  3: "Checkout",
});

const STEPS = [stepsEnum[0], stepsEnum[1], stepsEnum[2], stepsEnum[3]];
const STEPS_NO_EVENTS = [stepsEnum[0], stepsEnum[2], stepsEnum[3]];
const STEPS_MACHINE_TEST = [stepsEnum[0], stepsEnum[2], stepsEnum[3]]; // Cart - Address - Checkout for machine test (no Event step)
const STEPS_NORMAL_EVENT = [stepsEnum[1], stepsEnum[2], stepsEnum[3]]; // Event - Address - Checkout for normal event flow

const checkoutSlice = createSlice({
  name: "checkout",
  initialState: {
    checkoutSteps: STEPS,
    hasEvents: false,
    hasProducts: false,
    isMachineTestMode: false,
    isNormalEventMode: false,
    totalPrice: 0,
    tncEventsChecked: false,
    tncProductsChecked: false,
  },
  reducers: {
    setHasEvents(state, action) {
      state.hasEvents = action.payload;
      // Only set regular steps if not in machine test mode or normal event mode
      if (!state.isMachineTestMode && !state.isNormalEventMode) {
        if (state.hasEvents) {
          state.checkoutSteps = STEPS;
        } else {
          state.checkoutSteps = STEPS_NO_EVENTS;
        }
      }
    },
    setHasProducts(state, action) {
      state.hasProducts = action.payload;
    },
    setCheckoutSteps(state, action) {
      state.checkoutSteps = action.payload;
    },
    setMachineTestSteps(state) {
      state.checkoutSteps = STEPS_MACHINE_TEST;
      state.isMachineTestMode = true;
    },
    setNormalEventSteps(state) {
      state.checkoutSteps = STEPS_NORMAL_EVENT;
      state.isMachineTestMode = false;
      state.isNormalEventMode = true;
    },
    resetMachineTestMode(state) {
      state.isMachineTestMode = false;
      state.isNormalEventMode = false;
    },
    setTotalPrice(state, action) {
      state.totalPrice = action.payload;
    },
    setTncEventsChecked(state, action) {
      state.tncEventsChecked = action.payload;
    },
    setTncProductsChecked(state, action) {
      state.tncProductsChecked = action.payload;
    },
  },
});

export const {
  setHasEvents,
  setHasProducts,
  setCheckoutSteps,
  setMachineTestSteps,
  setNormalEventSteps,
  resetMachineTestMode,
  setTotalPrice,
  setTncEventsChecked,
  setTncProductsChecked,
} = checkoutSlice.actions;

export const manageHasProductsAsync = createAsyncThunk(
  "checkout/ManageHasProducts",
  async (_, thunkAPI) => {
    try {
      const user_id = localStorage.getItem("user_id");
      const eventItemsUrl = `${BASE_URL}cart/getAllMyCartDataForEvents/${user_id}`;
      const eventItemsData = await (await fetch(eventItemsUrl)).json();

      const machineTestItemsUrl = `${BASE_URL}cart/getAllMyCartDataForMachineTestEvents/${user_id}`;
      const machineTestItemsData = await (
        await fetch(machineTestItemsUrl)
      ).json();

      if (eventItemsData.length + machineTestItemsData.length > 0) {
        thunkAPI.dispatch(setHasEvents(true));
      } else {
        thunkAPI.dispatch(setHasEvents(false));
        thunkAPI.dispatch(setTncEventsChecked(true));
      }

      thunkAPI.dispatch(getCartTotalPriceAsync());
    } catch (error) {
      console.log(error);
    }
  }
);

export const manageHasEventsAsync = createAsyncThunk(
  "checkout/ManageHasEvents",
  async (_, thunkAPI) => {
    try {
      const user_id = localStorage.getItem("user_id");
      const eventItemsUrl = `${BASE_URL}cart/getAllMyCartDataForEvents/${user_id}`;
      const eventItemsData = await (await fetch(eventItemsUrl)).json();

      const machineTestItemsUrl = `${BASE_URL}cart/getAllMyCartDataForMachineTestEvents/${user_id}`;
      const machineTestItemsData = await (
        await fetch(machineTestItemsUrl)
      ).json();

      if (eventItemsData.length + machineTestItemsData.length > 0) {
        thunkAPI.dispatch(setHasEvents(true));
      } else {
        thunkAPI.dispatch(setHasEvents(false));
        thunkAPI.dispatch(setTncEventsChecked(true));
      }

      thunkAPI.dispatch(getCartTotalPriceAsync());
    } catch (error) {
      console.log(error);
    }
  }
);

export const getCartTotalPriceAsync = createAsyncThunk(
  "checkout/GetCartTotalPrice",
  async (_, thunkAPI) => {
    try {
      const user_id = localStorage.getItem("user_id");
      
      // Check if we're in event booking flow (for both logged-in and guest users)
      const loggedInEventBooking = localStorage.getItem('booking_event');
      const guestEventBooking = localStorage.getItem('booking_event_guest');
      
      
      if (loggedInEventBooking || guestEventBooking) {
        const eventBookingData = loggedInEventBooking 
          ? JSON.parse(loggedInEventBooking)
          : JSON.parse(guestEventBooking);
        
        // Calculate event price
        const parentPrice = parseFloat(eventBookingData.price || 0) * parseInt(eventBookingData.parent || 0);
        const childPrice = parseFloat(eventBookingData.child_price || eventBookingData.price || 0) * parseInt(eventBookingData.child || 0);
        const eventTotal = parentPrice + childPrice;
        
        thunkAPI.dispatch(setTotalPrice(eventTotal));
        thunkAPI.dispatch(setHasProducts(false)); 
        return;
      }
      
      // Handle guest users (normal cart flow)
      if (!user_id || user_id <= 0) {
        // For guest users, calculate total from localStorage
        const guestProducts = JSON.parse(localStorage.getItem('guest_cart_products') || '[]');
        const guestMagazines = JSON.parse(localStorage.getItem('guest_cart_magazines') || '[]');
        const guestEvents = JSON.parse(localStorage.getItem('guest_cart_events') || '[]');
        const guestMachineTests = JSON.parse(localStorage.getItem('guest_mt_cart') || '[]');
        
        let totalPrice = 0;
        
        // Calculate product prices
        guestProducts.forEach(item => {
          totalPrice += parseFloat(item.price || 0) * parseInt(item.quantity || 1);
        });
        
        // Calculate magazine prices (if available in data)
        guestMagazines.forEach(item => {
          totalPrice += parseFloat(item.price || 0) * parseInt(item.quantity || 1);
        });
        
        // Calculate event prices
        guestEvents.forEach(item => {
          const parentPrice = parseFloat(item.price || 0) * parseInt(item.parent || 0);
          const childPrice = parseFloat(item.child_price || item.price || 0) * parseInt(item.child || 0);
          totalPrice += parentPrice + childPrice;
        });
        
        // Calculate machine test prices
        guestMachineTests.forEach(item => {
          const parentPrice = parseFloat(item.price || item.first_price || 0) * parseInt(item.parent || 0);
          const childPrice = parseFloat(item.child_price || item.second_price || item.price || 0) * parseInt(item.child || 0);
          totalPrice += parentPrice + childPrice;
        });
        
        thunkAPI.dispatch(setTotalPrice(totalPrice));
        
        if (guestProducts.length > 0) {
          thunkAPI.dispatch(setHasProducts(true));
        } else {
          thunkAPI.dispatch(setHasProducts(false));
        }
        
        return;
      }
      
      // Handle logged-in users (normal cart flow)
      const cartTotalUrl = `${BASE_URL}cart/getCartTotalPrice/${user_id}`;
      const cartTotalData = await (await fetch(cartTotalUrl)).json();

      const productsUrl = `${BASE_URL}cart/getAllMyCartData/${user_id}`;
      const productsData = await (await fetch(productsUrl)).json();

      if (productsData.length) {
        thunkAPI.dispatch(setHasProducts(true));

        if (productsData[0].total_shipping_charges) {
          const result =
            cartTotalData?.cart_total_price -
            +productsData[0].total_shipping_charges;
          thunkAPI.dispatch(setTotalPrice(result));
          return;
        }

        thunkAPI.dispatch(setTotalPrice(cartTotalData?.cart_total_price));
      } else {
        thunkAPI.dispatch(setHasProducts(false));
        thunkAPI.dispatch(setTotalPrice(cartTotalData?.cart_total_price));
      }
    } catch (error) {
      console.log(error);
    }
  }
);

export const selectCheckoutSteps = (state) => state.checkout.checkoutSteps;
export const selectHasEvents = (state) => state.checkout.hasEvents;
export const selectHasProducts = (state) => state.checkout.hasProducts;
export const selectTotalPrice = (state) => state.checkout.totalPrice;
export const selectTncEventsChecked = (state) =>
  state.checkout.tncEventsChecked;
export const selectTncProductsChecked = (state) =>
  state.checkout.tncProductsChecked;

export default checkoutSlice.reducer;
